import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  signupWithEmail,
  signupWithGoogle,
  validateInviteToken,
  friendlyAuthError,
  routeAfterAuth,
  SignupPayload,
} from '../../services/auth-service';
import { Spinner } from '../../components';

// ============================================================================
// Types
// ============================================================================

type Step = 'role' | 'form' | 'plan' | 'processing' | 'pending';
type Role = 'founder' | 'partner' | 'associate' | 'investor' | 'mentor';
type Plan = 'solo' | 'team';

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  // role-specific (optional)
  startupName?: string;
  stage?: string;
  institution?: string;
  titleRole?: string;
  studentCount?: string;
  organization?: string;
  purpose?: string;
  fundName?: string;
  checkSize?: string;
}

interface RoleConfig {
  id: Role;
  name: string;
  tagline: string;
  perks: string;
  approval: boolean;
  Icon: React.FC<{ className?: string }>;
  accent: string; // background tint class
  hidden?: boolean; // not shown in the role grid (e.g. mentor — invite-only)
}

interface FieldDef {
  name: keyof FormData;
  label: string;
  type: 'text' | 'select';
  options?: string[];
  placeholder?: string;
  optional?: boolean;
  maxLength?: number;
}

// ============================================================================
// Icons
// ============================================================================

const ChevronLeft: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const EyeIcon: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const GoogleIcon: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const RocketIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const BuildingIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
  </svg>
);

const BeakerIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
  </svg>
);

const TrendingUpIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.306a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);

// ============================================================================
// Role / plan config
// ============================================================================

const ROLES: RoleConfig[] = [
  {
    id: 'founder',
    name: 'Founder',
    tagline: 'Build your startup through our 6-phase virtual incubation. Solo or with a team.',
    perks: 'Courses, mentorship, certificates, investor pitches',
    approval: false,
    Icon: RocketIcon,
    accent: 'bg-chart/10 text-chart',
  },
  {
    id: 'partner',
    name: 'Partner',
    tagline: 'Universities and institutions that want a read-only dashboard of their student founders.',
    perks: 'Student dashboards, cohort analytics, data exports',
    approval: true,
    Icon: BuildingIcon,
    accent: 'bg-success/10 text-success',
  },
  {
    id: 'associate',
    name: 'Associate',
    tagline: "Researchers, policy teams, and government bodies tracking Pakistan's startup ecosystem.",
    perks: 'Anonymized cohort data, quarterly insights',
    approval: true,
    Icon: BeakerIcon,
    accent: 'bg-warning/10 text-warning',
  },
  {
    id: 'investor',
    name: 'Investor',
    tagline: 'See curated pitches from vetted founders and schedule intros.',
    perks: 'Referred pitches, founder intros, portfolio tracker',
    approval: true,
    Icon: TrendingUpIcon,
    accent: 'bg-accent-soft text-chart',
  },
  {
    id: 'mentor',
    name: 'Mentor',
    tagline: 'Lead group mentor sessions and review founder pitches.',
    perks: 'Mentor dashboard, session scheduling, pitch reviews',
    approval: false,                     // invite already counts as approval
    Icon: BeakerIcon,
    accent: 'bg-role-mentor/10 text-role-mentor',
    hidden: true,                        // never shown in the role grid — invite-only
  },
];

const ROLE_BY_ID = (id: Role): RoleConfig => ROLES.find((r) => r.id === id)!;

const ROLE_DEMO: Record<Role, FormData> = {
  founder:   { name: '', email: '', password: '', confirmPassword: '' },
  partner:   { name: '', email: '', password: '', confirmPassword: '' },
  associate: { name: '', email: '', password: '', confirmPassword: '' },
  investor:  { name: '', email: '', password: '', confirmPassword: '' },
  mentor:    { name: '', email: '', password: '', confirmPassword: '' },
};

const ROLE_EXTRA_FIELDS: Record<Role, FieldDef[]> = {
  founder: [
    { name: 'startupName', label: 'Startup name or idea (optional)', type: 'text', placeholder: "One line on what you're building", optional: true, maxLength: 100 },
    { name: 'stage', label: 'Current stage', type: 'select', options: ['Just an idea', 'Prototype', 'MVP in production', 'Revenue'] },
  ],
  partner: [],
  associate: [],
  investor: [],
  mentor: [],
};

const EMPTY_FORM: FormData = { name: '', email: '', password: '', confirmPassword: '' };

// ============================================================================
// Shared input classes
// ============================================================================

const inputBase =
  'w-full px-4 py-2.5 rounded-card border bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-sm';

// ============================================================================
// Main component
// ============================================================================

const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planParam = searchParams.get('plan');
  const inviteTokenParam = searchParams.get('token');

  const { initialStep, initialRole, initialPlan } = useMemo(() => {
    if (planParam === 'solo' || planParam === 'team') {
      return { initialStep: 'form' as Step, initialRole: 'founder' as Role, initialPlan: planParam as Plan };
    }
    if (planParam === 'partner' || planParam === 'associate' || planParam === 'investor') {
      return { initialStep: 'form' as Step, initialRole: planParam as Role, initialPlan: null as Plan | null };
    }
    return { initialStep: 'role' as Step, initialRole: null as Role | null, initialPlan: null as Plan | null };
  }, [planParam]);

  const [step, setStep] = useState<Step>(initialStep);
  const [role, setRole] = useState<Role | null>(initialRole);
  const [formData, setFormData] = useState<FormData>(initialRole ? ROLE_DEMO[initialRole] : EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [submittingGoogle, setSubmittingGoogle] = useState(false);
  const submitting = submittingEmail || submittingGoogle;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteEmailLocked, setInviteEmailLocked] = useState(false);

  // initialPlan is no longer used — plan selection happens on /plans after verification.
  // Keep the param parsing so old marketing links (?plan=solo) still land on the founder form.
  void initialPlan;

  // ---- Invite token resolution ----
  useEffect(() => {
    if (!inviteTokenParam) return;
    let alive = true;
    (async () => {
      try {
        const info = await validateInviteToken(inviteTokenParam);
        if (!alive) return;
        setRole(info.role as Role);
        setInviteToken(inviteTokenParam);
        setInviteEmailLocked(true);
        setFormData({
          ...EMPTY_FORM,
          name: info.name || '',
          email: info.email || '',
        });
        setStep('form');
      } catch (err: any) {
        if (alive) setSubmitError(friendlyAuthError(err) || 'Invite link is invalid or has expired.');
      }
    })();
    return () => { alive = false; };
  }, [inviteTokenParam]);

  const navigateAfterAuth = (
    res: { user: any; requiresEmailVerification: boolean },
    fallbackEmail: string,
  ) => {
    const target = routeAfterAuth(res.user, { requiresEmailVerification: res.requiresEmailVerification });
    if (target === '/verify') {
      navigate('/verify', { state: { email: fallbackEmail || res.user.email, role: res.user.role } });
    } else {
      navigate(target);
    }
  };

  // ---- Actions ----
  const buildPayload = (): SignupPayload => {
    if (!role) throw new Error('No role selected');
    const base: SignupPayload = {
      name: formData.name,
      userType: role,
      inviteToken: inviteToken || undefined,
    };
    if (role === 'founder') {
      // Plan is selected on /plans after email verification — no longer part of signup payload.
      base.stage = formData.stage;
      base.startupName = formData.startupName;
    } else if (role === 'partner') {
      base.institution = formData.institution;
      base.titleRole = formData.titleRole;
      base.studentCount = formData.studentCount;
    } else if (role === 'associate') {
      base.organization = formData.organization;
      base.titleRole = formData.titleRole;
      base.purpose = formData.purpose;
    } else if (role === 'investor') {
      base.fundName = formData.fundName;
      base.titleRole = formData.titleRole;
      base.checkSize = formData.checkSize;
    }
    return base;
  };

  const pickRole = (r: Role) => {
    setRole(r);
    setFormData(ROLE_DEMO[r] || EMPTY_FORM);
    setErrors({});
    setSubmitError(null);
    setInviteToken(null);
    setInviteEmailLocked(false);
    setStep('form');
  };

  const backToRoleStep = () => {
    setStep('role');
    setRole(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError(null);
    setInviteToken(null);
    setInviteEmailLocked(false);
  };

  const updateField = (name: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as string]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name as string];
        return n;
      });
    }
  };

  const validateForm = (opts: { skipPassword?: boolean; skipEmail?: boolean } = {}): boolean => {
    if (!role) return false;
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!opts.skipEmail && !formData.email.trim()) errs.email = 'Email is required';
    if (!opts.skipPassword) {
      if (!formData.password) errs.password = 'Password is required';
      if (formData.password && formData.password.length < 6) errs.password = 'Min 6 characters';
      if (!formData.confirmPassword) errs.confirmPassword = 'Please confirm your password';
      if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
        errs.confirmPassword = 'Passwords do not match';
      }
    }
    for (const f of ROLE_EXTRA_FIELDS[role]) {
      if (!f.optional && !(formData as unknown as Record<string, string | undefined>)[f.name as string]) {
        errs[f.name as string] = 'Required';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Email/password submit
  // Email/password submit — same flow for every role now. The server-decided
  // routing (verify → pending → plans → dashboard) is centralized in routeAfterAuth.
  const onSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitError(null);
    setSubmittingEmail(true);
    try {
      const res = await signupWithEmail(formData.email, formData.password, buildPayload());
      navigateAfterAuth(res, formData.email);
    } catch (err: any) {
      const msg = friendlyAuthError(err);
      if (msg) setSubmitError(msg);
    } finally {
      setSubmittingEmail(false);
    }
  };

  // Google signup — no form fields required; name/avatar are pulled from the
  // Google account automatically after the popup resolves.
  const onGoogleShortcut = async () => {
    if (!role) return;
    setSubmitError(null);
    setSubmittingGoogle(true);
    try {
      const res = await signupWithGoogle(buildPayload());
      navigateAfterAuth(res, res.user.email);
    } catch (err: any) {
      const msg = friendlyAuthError(err);
      if (msg) setSubmitError(msg);
    } finally {
      setSubmittingGoogle(false);
    }
  };

  // ---- Render ----
  const showLeftPanel = step === 'form' && role !== null;

  return (
    <div className="min-h-screen flex bg-bg">
      {/* ─── Left decorative panel (only on form step) ─── */}
      {showLeftPanel && role && <LeftPanel role={role} />}

      {/* ─── Right content area ─── */}
      <div className="flex-1 bg-bg flex flex-col items-center p-6 lg:p-12 overflow-y-auto">
        {/* Top nav bar */}
        {step !== 'processing' && (
          <div className="w-full max-w-5xl mx-auto mb-6 flex items-center justify-between">
            <TopBackButton step={step} onBackToRole={backToRoleStep} />
            {!showLeftPanel && (
              <Link to="/" className="hidden sm:inline-flex items-center">
                <img src="/logo.png" alt="BunyadKar" className="h-8 w-auto opacity-80" />
              </Link>
            )}
          </div>
        )}

        {/* Step content */}
        <div className="w-full flex-1 flex flex-col items-center justify-start animate-fade-in">
          {step === 'role' && <RoleStep onPick={pickRole} />}

          {step === 'form' && role && (
            <FormStep
              role={role}
              formData={formData}
              errors={errors}
              showPassword={showPassword}
              showConfirm={showConfirm}
              setShowPassword={setShowPassword}
              setShowConfirm={setShowConfirm}
              onChangeField={updateField}
              onSubmit={onSubmitForm}
              onGoogle={onGoogleShortcut}
              submitting={submitting}
              submittingEmail={submittingEmail}
              submittingGoogle={submittingGoogle}
              submitError={submitError}
              emailLocked={inviteEmailLocked}
              isInviteFlow={!!inviteToken}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default Register;

// ============================================================================
// Subcomponents (local to this file)
// ============================================================================

const TopBackButton: React.FC<{
  step: Step;
  onBackToRole: () => void;
}> = ({ step, onBackToRole }) => {
  const base = 'inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors';
  if (step === 'role') {
    return (
      <Link to="/" className={base}>
        <ChevronLeft /> Back to home
      </Link>
    );
  }
  if (step === 'form') {
    return (
      <button type="button" onClick={onBackToRole} className={base}>
        <ChevronLeft /> Change role
      </button>
    );
  }
  return null;
};

// ---- Left panel for form step ----
const LeftPanel: React.FC<{ role: Role }> = ({ role }) => {
  const cfg = ROLE_BY_ID(role);
  const Icon = cfg.Icon;
  return (
    <div className="hidden lg:flex lg:w-[42%] bg-accent-soft relative overflow-hidden flex-col items-center justify-center p-12">
      <img
        src="/homepage-graphics/bubble-1.png"
        alt=""
        aria-hidden="true"
        className="absolute -top-20 -right-16 w-[420px] h-[420px] object-contain opacity-50 pointer-events-none select-none"
      />
      <div className="absolute bottom-12 left-8 w-32 h-32 rounded-full border-2 border-chart/20" />
      <div className="absolute bottom-8 left-4 w-20 h-20 rounded-full border border-chart/10" />

      <div className="relative z-10 text-center space-y-6 max-w-xs">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${cfg.accent}`}>
          <Icon className="w-10 h-10" />
        </div>
        <div>
          <div className="inline-block mb-2 px-3 py-1 rounded-full bg-shell border border-line text-xs font-medium tracking-wide uppercase text-muted">
            Registering as
          </div>
          <h2 className="font-display text-4xl tracking-wide text-text uppercase">{cfg.name}</h2>
          <p className="font-serif-display text-xl italic text-muted mt-3 leading-relaxed">{cfg.tagline}</p>
        </div>
        {cfg.approval && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-warning/15 text-warning text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-warning" />
            Admin approval required
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Step 1: Role selection ----
const RoleStep: React.FC<{ onPick: (r: Role) => void }> = ({ onPick }) => {
  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="text-center mb-10">
        <h1 className="font-display text-4xl md:text-5xl tracking-wide text-text uppercase mb-3">Join BunyadKar</h1>
        <p className="font-serif-display text-xl md:text-2xl italic text-muted max-w-2xl mx-auto">
          Tell us who you are — we'll tailor the rest.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {ROLES.filter(r => !r.hidden).map((r) => {
          const Icon = r.Icon;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onPick(r.id)}
              className="group relative flex flex-col h-full text-left rounded-card bg-shell border border-line hover:border-chart/40 hover:shadow-md transition-all duration-200 p-6 focus:outline-none focus:ring-2 focus:ring-chart/30"
            >
              {r.approval && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warning/15 text-warning text-[10px] font-medium tracking-wide uppercase">
                  <span className="w-1 h-1 rounded-full bg-warning" />
                  Approval
                </span>
              )}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${r.accent}`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-2xl tracking-wide text-text uppercase mb-2">{r.name}</h3>
              <p className="font-serif-display text-base italic text-muted leading-snug mb-4 flex-1">{r.tagline}</p>
              <div className="border-t border-line pt-3 mt-auto">
                <p className="text-xs text-muted leading-relaxed">
                  <span className="text-text font-medium">What you get · </span>
                  {r.perks}
                </p>
              </div>
              <div className="mt-4 flex items-center text-chart font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                Continue <span className="ml-1">→</span>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-center text-sm text-muted mt-10">
        Already have an account?{' '}
        <Link to="/login" className="text-chart hover:text-chart/80 font-medium transition-colors">
          Log in
        </Link>
      </p>
    </div>
  );
};

// ---- Step 2: Form ----
const FormStep: React.FC<{
  role: Role;
  formData: FormData;
  errors: Record<string, string>;
  showPassword: boolean;
  showConfirm: boolean;
  setShowPassword: (v: boolean) => void;
  setShowConfirm: (v: boolean) => void;
  onChangeField: (name: keyof FormData, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onGoogle: () => void;
  submitting?: boolean;
  submittingEmail?: boolean;
  submittingGoogle?: boolean;
  submitError?: string | null;
  emailLocked?: boolean;
  isInviteFlow?: boolean;
}> = ({ role, formData, errors, showPassword, showConfirm, setShowPassword, setShowConfirm, onChangeField, onSubmit, onGoogle, submitting, submittingEmail, submittingGoogle, submitError, emailLocked, isInviteFlow }) => {
  const cfg = ROLE_BY_ID(role);
  const isFounder = role === 'founder';

  return (
    <div className="w-full max-w-md">
      {/* Mobile role indicator */}
      <div className="lg:hidden flex items-center gap-3 mb-6 p-4 rounded-card bg-surface border border-line">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cfg.accent} flex-shrink-0`}>
          <cfg.Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted font-medium">Registering as</div>
          <div className="font-display text-lg tracking-wide text-text">{cfg.name}</div>
        </div>
      </div>

      <h1 className="font-display text-3xl md:text-4xl tracking-wide text-text mb-1">Create your account</h1>
      <p className="text-muted text-sm mb-6">
        {isFounder
          ? "Verify your email next, then pick a plan."
          : cfg.approval
          ? 'After submitting, your application will go to our team for approval.'
          : 'Fill out a few details to get started.'}
      </p>

      {isInviteFlow && (
        <div className="mb-4 px-3 py-2 rounded-card bg-success/10 border border-success/30 text-success text-xs">
          You were invited to BunyadKar. Email pre-filled below — pick a password or sign up with Google to accept.
        </div>
      )}

      {/* Google shortcut */}
      <button
        type="button"
        onClick={onGoogle}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-3 bg-shell border border-line py-2.5 rounded-card font-medium text-text hover:bg-surface transition-all duration-200 text-sm shadow-sm mb-4 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submittingGoogle ? <Spinner className="w-4 h-4 text-text" /> : <GoogleIcon />}
        {submittingGoogle ? 'Signing up…' : 'Sign up with Google'}
      </button>

      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-line" />
        <span className="text-xs text-muted">or fill out the form</span>
        <div className="flex-1 h-px bg-line" />
      </div>

      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {/* Common: name + email */}
        <div>
          <label className="block text-xs font-medium text-text mb-1.5">Full name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onChangeField('name', e.target.value)}
            placeholder="Your full name"
            className={`${inputBase} ${errors.name ? 'border-danger' : 'border-line'}`}
          />
          {errors.name && <p className="text-danger text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-text mb-1.5">Email address</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => onChangeField('email', e.target.value)}
            placeholder="you@example.com"
            disabled={!!emailLocked}
            className={`${inputBase} ${errors.email ? 'border-danger' : 'border-line'} ${emailLocked ? 'bg-surface/60 cursor-not-allowed' : ''}`}
          />
          {errors.email && <p className="text-danger text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Common: password + confirm */}
        <div>
          <label className="block text-xs font-medium text-text mb-1.5">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => onChangeField('password', e.target.value)}
              placeholder="Min. 6 characters"
              className={`${inputBase} pr-10 ${errors.password ? 'border-danger' : 'border-line'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text transition-colors"
              tabIndex={-1}
              aria-label="Toggle password visibility"
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          {errors.password
            ? <p className="text-danger text-xs mt-1">{errors.password}</p>
            : <p className="text-muted text-xs mt-1">At least 8 characters, with one uppercase letter and one number.</p>
          }
        </div>

        <div>
          <label className="block text-xs font-medium text-text mb-1.5">Confirm password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => onChangeField('confirmPassword', e.target.value)}
              placeholder="Re-enter your password"
              className={`${inputBase} pr-10 ${errors.confirmPassword ? 'border-danger' : 'border-line'}`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text transition-colors"
              tabIndex={-1}
              aria-label="Toggle password visibility"
            >
              <EyeIcon open={showConfirm} />
            </button>
          </div>
          {errors.confirmPassword && <p className="text-danger text-xs mt-1">{errors.confirmPassword}</p>}
        </div>

        {/* Role-specific fields */}
        {ROLE_EXTRA_FIELDS[role].length > 0 && (
          <div className="pt-2 mt-2 border-t border-line">
            <div className="text-xs font-medium text-muted uppercase tracking-wide mb-3">About you</div>
            <div className="space-y-4">
              {ROLE_EXTRA_FIELDS[role].map((field) => {
                const value = (formData as unknown as Record<string, string | undefined>)[field.name as string] || '';
                const hasErr = !!errors[field.name as string];
                if (field.type === 'select') {
                  return (
                    <div key={field.name as string}>
                      <label className="block text-xs font-medium text-text mb-1.5">{field.label}</label>
                      <select
                        value={value}
                        onChange={(e) => onChangeField(field.name, e.target.value)}
                        className={`${inputBase} ${hasErr ? 'border-danger' : 'border-line'} cursor-pointer`}
                      >
                        <option value="" disabled>
                          Select…
                        </option>
                        {field.options?.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      {hasErr && <p className="text-danger text-xs mt-1">{errors[field.name as string]}</p>}
                    </div>
                  );
                }
                return (
                  <div key={field.name as string}>
                    <label className="block text-xs font-medium text-text mb-1.5">{field.label}</label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => onChangeField(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      maxLength={field.maxLength}
                      className={`${inputBase} ${hasErr ? 'border-danger' : 'border-line'}`}
                    />
                    {hasErr && <p className="text-danger text-xs mt-1">{errors[field.name as string]}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {submitError && (
          <p className="text-danger text-xs">{submitError}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-chart text-white py-3 rounded-card font-medium hover:bg-chart/90 transition-all duration-200 shadow-sm text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submittingEmail && <Spinner className="w-4 h-4" />}
          {submittingEmail ? 'Creating account…' : (isFounder ? 'Continue →' : 'Submit Application')}
        </button>
      </form>

      <p className="text-center text-sm text-muted mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-chart hover:text-chart/80 font-medium transition-colors">
          Log in
        </Link>
      </p>
    </div>
  );
};
